import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowRight, Zap, Shield, SquareDashedBottomCode } from 'lucide-react';

/* ───── Animate-in hook ───── */
const useAnimateIn = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

/* ════════════════════════ HERO ════════════════════════ */
const Hero = () => (
  <section className="bg-white pt-24 lg:pt-36 pb-16 lg:pb-24">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
      <div className="text-center">
        {/* Pill badge */}
        <div className="inline-flex items-center gap-2 border border-gray-200 rounded-full px-5 py-2 mb-8">
          <span className="text-gray-500 text-[14px] font-normal">Duty AI</span>
          <span className="text-gray-300">/</span>
          <span className="text-gray-800 text-[14px] font-medium">Enterprise</span>
        </div>

        <h1 className="text-[40px] sm:text-[56px] lg:text-[72px] font-normal leading-[1.05] tracking-[-0.03em] mb-6">
          <span className="text-gray-300">Missing layer:</span>
          <br />
          <span className="text-[#0F172A]">Your definition of "good"</span>
        </h1>

        <p className="text-gray-500 text-[16px] lg:text-[18px] font-normal leading-[1.6] max-w-[620px] mx-auto mb-10">
          Generic models are powerful but don't know how your teams work.
          <br className="hidden sm:block" />
          Like an employee, enterprise AI needs to be onboarded on your tools, workflows,
          <br className="hidden sm:block" />
          and standards — with built-in feedback infrastructure that compounds learning over time.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Link
            to="/demo"
            className="inline-flex items-center justify-center px-8 py-3.5 bg-[#6C5CE7] text-white text-[15px] font-medium rounded-full hover:bg-[#5B4BD5] transition-colors"
          >
            Get in touch
          </Link>
          <a
            href="#platform"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-gray-300 text-[#0F172A] text-[15px] font-medium rounded-full hover:bg-gray-50 transition-colors"
          >
            How it works
          </a>
        </div>

        <Link
          to="/blog/we-raised-20m-to-help-ai-hire-more-humans"
          className="inline-flex items-center gap-1 text-[#6C5CE7] text-[14px] font-medium hover:underline transition-all"
        >
          Read the full post: Introducing Duty AI Enterprise AI <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);

/* ════════════════════════ STATS BAR ════════════════════════ */
const stats = [
  { value: '$10B', label: 'Valuation' },
  { value: '$1B+', label: 'Revenue Run Rate' },
  { value: '100k+', label: 'Contractors Hired' },
  { value: '400+', label: 'Full-time Employees' },
];

const StatsBar = () => {
  const [ref, visible] = useAnimateIn();
  return (
    <section ref={ref} className="bg-white py-12 lg:py-16">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
          {stats.map((s, i) => (
            <div
              key={i}
              className={`text-center py-6 lg:py-4 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="text-[#0F172A] text-[36px] lg:text-[48px] font-normal leading-none mb-1 tracking-[-0.02em]">
                {s.value}
              </div>
              <div className="text-gray-500 text-[14px] font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Divider line */}
      <div className="max-w-[1200px] mx-auto mt-12">
        <div className="border-t border-gray-100" />
      </div>
    </section>
  );
};

/* ════════════════════════ PLATFORM CARDS ════════════════════════ */
const platformSteps = [
  { title: 'Absorb organizational context', desc: 'Map workflows, ingest company knowledge, and conduct AI-moderated interviews to build your organizational context graph.', week: 'WEEK 1–2' },
  { title: 'Specify agent behavior', desc: 'Turn your context graph into programmatically defined agent behavior specs and quality guardrails.', week: 'WEEK 2–3' },
  { title: 'Deploy agent', desc: 'Deploy agents using any model, agent framework, or internal system your organization already uses.', week: 'WEEK 3–4' },
  { title: 'Iterate on failures', desc: 'Catch agent failures and feed automated and human-driven corrections back into the system, compounding accuracy over time.', week: 'WEEK 4+' },
];

const PlatformSection = () => {
  const [ref, visible] = useAnimateIn();
  return (
    <section ref={ref} id="platform" className="bg-white py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        {/* Pill badge */}
        <div className="flex justify-center mb-5">
          <span className="inline-block border border-gray-200 rounded-full px-5 py-1.5 text-gray-500 text-[13px] font-medium">
            Platform
          </span>
        </div>

        <h2 className="text-[#0F172A] text-[32px] lg:text-[48px] font-normal leading-[1.1] tracking-[-0.02em] mb-4 text-center">
          Build AI agents designed for your company
        </h2>
        <p className="text-center text-gray-500 text-[16px] lg:text-[17px] max-w-[700px] mx-auto mb-12 lg:mb-16 font-normal">
          Weeks, not years. Our platform converts your internal data, workflows, and domain expertise into agents that operate like your team.
        </p>

        {/* Purple banner */}
        <div className="bg-[#6C5CE7] rounded-t-xl py-3 px-6 text-center">
          <span className="text-white text-[12px] lg:text-[13px] font-semibold tracking-[0.1em] uppercase">
            Duty AI Software Platform
          </span>
        </div>

        {/* 4-card grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-gray-200 border-t-0 rounded-b-xl overflow-hidden">
          {platformSteps.map((step, i) => (
            <div
              key={i}
              className={`p-6 lg:p-7 border-b sm:border-b-0 sm:border-r border-gray-200 last:border-r-0 flex flex-col justify-between transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              {/* Dot */}
              <div>
                <div className="w-3 h-3 rounded-full bg-gray-300 mb-8" />
                <h3 className="text-[#6C5CE7] text-[16px] lg:text-[17px] font-semibold mb-3 leading-[1.3]">
                  {step.title}
                </h3>
                <p className="text-gray-500 text-[14px] leading-[1.6] mb-8">
                  {step.desc}
                </p>
              </div>
              <span className="text-gray-400 text-[12px] font-medium tracking-[0.05em] uppercase">
                {step.week}
              </span>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-400 text-[14px] mt-10 max-w-[750px] mx-auto font-normal italic">
          Every deployment is supported by world-class AI research and engineering talent, on the ground delivery, and oversight from senior leadership with experience at the frontier of AI deployment.
        </p>
      </div>
    </section>
  );
};

/* ════════════════════════ INSIGHT SECTION ════════════════════════ */
const InsightSection = () => {
  const [ref, visible] = useAnimateIn();
  return (
    <section ref={ref} className="bg-white py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        {/* Divider */}
        <div className="border-t border-gray-100 mb-16" />

        <h2 className={`text-center text-[28px] lg:text-[44px] font-normal leading-[1.15] tracking-[-0.02em] mb-4 max-w-[900px] mx-auto transition-all duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          <span className="text-gray-300">Enterprise AI doesn't fail from weak models.</span>
          <br />
          <span className="text-[#0F172A]">Enterprise AI fails from not knowing what "good" looks like.</span>
        </h2>

        <p className="text-center text-gray-400 text-[15px] max-w-[550px] mx-auto mb-14 italic">
          We built this system for every leading AI lab. Now we bring the same infrastructure to enterprise.
        </p>

        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Zap, title: 'Rapid Agent Iteration', desc: 'Quickly identify gaps in agent performance and optimize prompts in tight feedback loops.' },
            { icon: SquareDashedBottomCode, title: 'Eliminate Silent Failures', desc: 'Surface errors with clear paths for correction — no more invisible mistakes compounding.' },
            { icon: Shield, title: 'Safe Deployment', desc: 'Guardrails and observability de-risk deployment in high-stakes processes requiring nuanced judgment.' },
          ].map((item, i) => (
            <div
              key={i}
              className={`bg-white rounded-2xl p-8 border border-gray-200 transition-all duration-700 hover:shadow-md ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className="mb-6">
                <item.icon className="w-8 h-8 text-[#0F172A]" strokeWidth={1.5} />
              </div>
              <h3 className="text-[#0F172A] text-[18px] lg:text-[20px] font-semibold mb-2">{item.title}</h3>
              <p className="text-gray-500 text-[14px] leading-[1.7]">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ════════════════════════ USE CASES ════════════════════════ */
const useCaseColors = [
  'from-[#8B7AF5] to-[#6C5CE7]',
  'from-[#7B6BF0] to-[#5B4BD5]',
  'from-[#9B8BFA] to-[#7C6CF0]',
  'from-[#A99BFF] to-[#8B7AF5]',
  'from-[#B8ABFF] to-[#9B8BFA]',
];

const enterpriseUseCases = [
  { title: 'Recruiting', desc: 'Source, screen, outreach, and update trackers across Slack, docs, and spreadsheets.' },
  { title: 'Business Operations', desc: 'Drive milestone reviews, track KPI performance, and coordinate scheduling across teams.' },
  { title: 'Engineering', desc: 'Handle high-volume dev workflows, write backend scripts, monitor pipelines, and debug failures.' },
  { title: 'Finance', desc: 'Streamline bulk payment workflows, extract obligations, coordinate stakeholders, and reconcile transactions.' },
  { title: 'Customer Service', desc: 'Resolve support tickets end-to-end with context-aware agents that learn and improve continuously.' },
];

const EnterpriseUseCases = () => {
  const [ref, visible] = useAnimateIn();
  return (
    <section ref={ref} className="bg-white py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        {/* Divider */}
        <div className="border-t border-gray-100 mb-16" />

        {/* Pill badge */}
        <div className="flex justify-center mb-5">
          <span className="inline-block border border-gray-200 rounded-full px-5 py-1.5 text-gray-500 text-[13px] font-medium">
            Use Cases
          </span>
        </div>

        <h2 className="text-[#0F172A] text-[32px] lg:text-[48px] font-normal leading-[1.1] tracking-[-0.02em] mb-4 text-center">
          Suited for every workflow
        </h2>
        <p className="text-center text-gray-500 text-[16px] lg:text-[17px] max-w-[600px] mx-auto mb-12 lg:mb-16 font-normal">
          Our framework generalizes across departments — wherever complex, multi-step work happens.
        </p>

        {/* Use case colored cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {enterpriseUseCases.map((uc, i) => (
            <div
              key={i}
              className={`relative bg-gradient-to-b ${useCaseColors[i]} rounded-2xl p-6 min-h-[220px] flex flex-col justify-end transition-all duration-700 hover:scale-[1.03] ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <h3 className="text-white text-[16px] lg:text-[18px] font-semibold mb-2">{uc.title}</h3>
              <p className="text-white/80 text-[13px] leading-[1.5]">{uc.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ════════════════════════ CASE STUDY ════════════════════════ */
const CaseStudy = () => {
  const [ref, visible] = useAnimateIn();
  return (
    <section ref={ref} className="bg-[#13142B] py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        {/* Pill badge */}
        <div className="flex justify-center mb-5">
          <span className="inline-block border border-gray-600 rounded-full px-5 py-1.5 text-gray-400 text-[13px] font-medium">
            Case Study
          </span>
        </div>

        <h2 className="text-white text-[32px] lg:text-[48px] font-normal leading-[1.1] tracking-[-0.02em] mb-4 text-center">
          Our first customer: Duty AI
        </h2>
        <p className="text-center text-gray-400 text-[16px] lg:text-[17px] max-w-[700px] mx-auto mb-14 font-normal">
          We ran it on ourselves first and dramatically outperformed existing workflows in customer success, recruiting, and IT support.
        </p>

        {/* Big stat cards */}
        <div className={`grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {[
            { value: '90%', label: 'Support resolved E2E by AI' },
            { value: '100k+', label: 'Contractors hired E2E by AI' },
            { value: '12k+', label: 'Requests resolved E2E by AI' },
          ].map((s, i) => (
            <div key={i} className="border border-gray-700 rounded-2xl p-8 text-center">
              <div className="text-white text-[40px] lg:text-[52px] font-normal leading-none mb-2 tracking-[-0.02em]">{s.value}</div>
              <div className="text-gray-500 text-[14px] font-normal">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Customer Support deep-dive */}
        <div className={`bg-[#1C1D36] rounded-2xl p-8 lg:p-12 border border-gray-700/50 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="flex-1">
              <h3 className="text-white text-[22px] lg:text-[26px] font-semibold mb-5">Customer Support Transformation</h3>
              <p className="text-gray-400 text-[15px] leading-[1.7] mb-6">
                System scaled from 5% AI resolution at launch to 90% E2E resolution as agents iterated automatically on evals that humans continuously enriched with context from escalated tickets
              </p>
              <div className="inline-flex items-center gap-2 bg-[#6C5CE7]/20 text-[#A99BFF] rounded-full px-4 py-1.5 text-[13px] font-medium">
                $1.4M saved
              </div>
            </div>
            <div className="flex-1">
              <div className="space-y-5">
                {[
                  { label: 'Rapid ROI, within 2 weeks', highlight: true },
                  { label: 'Close Time dropped by 73%', highlight: false },
                  { label: '70% of tickets were handled or assisted by agent', highlight: false },
                  { label: 'Outperformed humans', highlight: true },
                  { label: 'Agent CSAT outperformed our team', highlight: false },
                  { label: 'Today, agents have a 10% higher CSAT than human agents', highlight: false },
                  { label: 'Agent response time ~10min v. 2 days', highlight: false },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    {item.highlight ? (
                      <span className="text-white text-[14px] font-semibold">{item.label}</span>
                    ) : (
                      <span className="text-gray-400 text-[14px]">{item.label}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ════════════════════════ CTA ════════════════════════ */
const BottomCTA = () => (
  <section className="bg-white py-20 lg:py-32">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 text-center">
      <h2 className="text-[#0F172A] text-[36px] lg:text-[56px] font-normal leading-[1.05] tracking-[-0.03em] mb-5">
        Ready to empower your workforce?
      </h2>
      <p className="text-gray-500 text-[16px] lg:text-[17px] max-w-[550px] mx-auto mb-10 font-normal">
        From initial discovery to production-grade AI agents delivering measurable ROI in 4–6 weeks.
      </p>
      <Link
        to="/demo"
        className="inline-flex items-center justify-center px-10 py-3.5 bg-[#6C5CE7] text-white text-[15px] font-medium rounded-full hover:bg-[#5B4BD5] transition-colors mb-4"
      >
        Get in touch
      </Link>
      <p className="text-gray-400 text-[14px]">enterprise@dutyai.com</p>
    </div>
  </section>
);

/* ════════════════════════ PAGE ════════════════════════ */
const EnterprisePage = () => (
  <>
    <Navbar />
    <Hero />
    <StatsBar />
    <PlatformSection />
    <InsightSection />
    <EnterpriseUseCases />
    <CaseStudy />
    <BottomCTA />
    <Footer />
  </>
);

export default EnterprisePage;
